/**
 * What Quies draws itself on its feature page, because the visual is its own interface: the sessions with its own
 * player (`AudioPlayer`) and the walkthrough's screens (`SessionWalkthrough`), as the home has always shown them.
 * The walkthrough is drawn still, its controls out of reach (`inert`).
 */
import {useLocation} from 'react-router'
import type {SessionCard} from '@core/proof/model'
import type {ProofParts} from '@core/proof/ProofPage'
import {AudioPlayer} from '@product/quies/components/AudioPlayer'
import {SessionWalkthrough} from '@product/quies/components/SessionWalkthrough/SessionWalkthrough'
import {displayDuration} from '@product/quies/data/session-samples'
import {walkthroughContent} from '@product/quies/data/walkthrough-content'
import {useInterfaceStrings} from '@product/quies/i18n/catalogue'
import type {HomeLang} from '@product/quies/lib/homeLangs'
import styles from './parts.module.scss'

/** Paragraphs of a transcript shown before the rest folds. */
const TRANSCRIPT_OPEN = 2

function Transcript({session}: {session: SessionCard}) {
    const {t} = useInterfaceStrings()
    const [shown, rest] = [session.transcript.slice(0, TRANSCRIPT_OPEN), session.transcript.slice(TRANSCRIPT_OPEN)]
    return (
        <section lang={session.lang} className={styles.transcript}>
            <h4 lang={undefined}>{t.page.transcript}</h4>
            {shown.map((paragraph, i) => <p key={i}>{paragraph}</p>)}
            {rest.length > 0 && (
                <details>
                    <summary>{t.page.transcript}</summary>
                    {rest.map((paragraph, i) => <p key={i}>{paragraph}</p>)}
                </details>
            )}
        </section>
    )
}

function SessionRow({session, transcript, showLanguage}: {session: SessionCard; transcript: boolean; showLanguage: boolean}) {
    const {pathname} = useLocation()
    return (
        <article className={styles.session}>
            {showLanguage && <span className={styles.language}>{session.langName}</span>}
            <div className={styles.player} lang={session.lang}>
                <AudioPlayer
                    src={session.src}
                    title={session.title}
                    past
                    analytics={{surface: 'sample', item: `sample:${session.id}`, page: pathname}}
                />
                <span className={styles.length}>{displayDuration(session.seconds)}</span>
            </div>
            <p className={styles.writtenFor} lang={session.lang}>{session.writtenFor}</p>
            {transcript && <Transcript session={session} />}
        </article>
    )
}

/** Sessions of one language are listed by their words; sessions of several are named by their language. */
export function QuiesSessions({kind, sessions}: {kind: 'sessions' | 'listen'; sessions: SessionCard[]}) {
    const mixed = new Set(sessions.map((session) => session.lang)).size > 1
    return (
        <div className={styles.sessions}>
            {sessions.map((session) => (
                <SessionRow key={session.id} session={session} transcript={kind === 'listen'} showLanguage={mixed} />
            ))}
        </div>
    )
}

export function quiesParts(lang: HomeLang): ProofParts {
    return {
        sessions: (visual) => <QuiesSessions kind={visual.kind} sessions={visual.sessions} />,
        steps: () => (
            <div className={styles.walkthrough} inert>
                <SessionWalkthrough {...walkthroughContent.generic[lang]!} bare />
            </div>
        ),
    }
}
